/* eslint-disable no-unused-vars */
import React, { useMemo } from 'react';
import type { GridColDef } from '@mui/x-data-grid';
import {
  TableRow,
  TableCell,
  Checkbox,
  IconButton,
  Collapse,
  Box,
  Tooltip,
} from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';

import { AgColor } from '../../Styles/AgColor';
import { styleMappings } from '../../Styles/AgTypography';

export interface ExpandableRowProps {
  row: any;
  columns: GridColDef[];
  selectable: boolean;
  selected: boolean;
  onToggleSelect: (checked: boolean, id: string | number) => void;
  disableRowSelectionOnCheck?: boolean;
  disableRowSelectionOnClick?: boolean;
  isExpanded: boolean;
  onToggleExpand: (id: string | number) => void;
  getId: (row: any) => string | number;
  allowToSelectBoolean?: boolean;
  renderExpandedContent?: (row: any) => React.ReactNode;
  onRowClick?: (row: any) => void;
  showExpand?: boolean;
  disableExpandOn?: (row: any) => boolean;
}

const EllipsisCell: React.FC<{
  title: React.ReactNode;
  align?: 'left' | 'right' | 'center' | 'inherit' | 'justify';
  children: React.ReactNode;
}> = ({ title, align = 'left', children }) => {
  return (
    <Box
      component='span'
      sx={{
        display: 'block',
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        textAlign: align,
      }}
    >
      <Tooltip placement='top' title={title}>
        <span>{children}</span>
      </Tooltip>
    </Box>
  );
};

const ExpandableRow: React.FC<ExpandableRowProps> = ({
  row,
  columns,
  selectable,
  selected,
  onToggleSelect,
  disableRowSelectionOnClick,
  isExpanded,
  onToggleExpand,
  getId,
  allowToSelectBoolean,
  renderExpandedContent,
  onRowClick,
  showExpand,
  disableExpandOn,
}) => {
  const id = useMemo(() => getId(row), [getId, row]);

  const handleRowClick = (field?: string, value?: any) => {
    if (disableRowSelectionOnClick) {
      onRowClick && onRowClick(row);
      return;
    }
    if (field === 'custom-link') {
      onRowClick && onRowClick(row);
      return;
    }
    if (typeof value === 'boolean' && !allowToSelectBoolean) {
      onRowClick && onRowClick(row);
      return;
    }
    if (selectable) onToggleSelect(!selected, id);
    onRowClick && onRowClick(row);
  };

  const commonCellSx = {
    ...styleMappings['AP.Paragraph.Small.Regular'],
    borderBottom: 0,
    backgroundColor: selected ? AgColor.secondaryLight2 : 'transparent',
    py: 1,
    lineHeight: 1.5,
  } as const;

  return (
    <>
      <TableRow
        hover
        sx={{ '&:hover td': { backgroundColor: AgColor.grey3 }, height: 50 }}
      >
        {selectable && (
          <TableCell padding='checkbox' sx={{ ...commonCellSx, width: 48 }}>
            <Checkbox
              color='primary'
              checked={selected}
              onChange={(e) => onToggleSelect(e.target.checked, id)}
            />
          </TableCell>
        )}
        {columns.map((col, idx) => {
          const raw = (row as any)?.[col.field as any];
          const value = (col as any).valueGetter
            ? (col as any).valueGetter({ value: raw, row })
            : raw;
          const content = (col as any).renderCell
            ? (col as any).renderCell({ row, value, field: col.field })
            : value;
          const align = (col as any).align || 'left';
          const primitiveToString = (v: any) =>
            v === null || v === undefined
              ? ''
              : typeof v === 'string' ||
                typeof v === 'number' ||
                typeof v === 'boolean'
              ? String(v)
              : '';
          const tooltipTitle =
            primitiveToString(value) || primitiveToString(content as any);
          return (
            <TableCell
              key={String(col.field)}
              onClick={() => handleRowClick(String(col.field), value)}
              sx={{
                ...commonCellSx,
                cursor: 'pointer',
                minWidth: (col as any).minWidth,
                width: (col as any).width,
                borderRight:
                  idx === 0 ? `1px solid ${AgColor.grey4}` : undefined,
              }}
              align={align as any}
            >
              <EllipsisCell title={tooltipTitle} align={align as any}>
                {content as any}
              </EllipsisCell>
            </TableCell>
          );
        })}
        {showExpand && (
          <TableCell sx={{ ...commonCellSx, width: 40 }} align='center'>
            {(!disableExpandOn ||
              (disableExpandOn && !disableExpandOn(row))) && (
              <IconButton size='small' onClick={() => onToggleExpand(id)}>
                {isExpanded ? (
                  <ExpandLess fontSize='small' />
                ) : (
                  <ExpandMore fontSize='small' />
                )}
              </IconButton>
            )}
          </TableCell>
        )}
      </TableRow>
      {showExpand && (
        <TableRow>
          <TableCell
            colSpan={
              (columns?.length || 0) +
              (showExpand ? 1 : 0) +
              (selectable ? 1 : 0)
            }
            sx={{ p: 0, borderBottom: `1px solid ${AgColor.grey4}` }}
          >
            <Collapse in={isExpanded} timeout='auto' unmountOnExit>
              <Box sx={{ backgroundColor: AgColor.grey1, p: 0, m: 0 }}>
                {renderExpandedContent ? renderExpandedContent(row) : null}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

export default ExpandableRow;
